/**
 * Das Exportfenster. Zeigt vorher, was in die Datei geht und was dem
 * entgegensteht, erzeugt sie und laedt sie herunter. Darunter der Weg in
 * DATEV, Schritt fuer Schritt, belegt mit der DATEV-Hilfe.
 *
 * Der eine gefuellte Knopf ist "Exportieren". Danach ist der Auszug in einem
 * Zustand, den DATEV nicht zuruecknimmt — deshalb steht das hier auch.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler } from '../lib/api'
import { iban, zeitraum } from '../lib/format'
import { Fenster } from './Fenster'
import { DatevAnleitung } from './DatevAnleitung'

interface Vorschau {
  konten: {
    iban: string
    inland: boolean
    version: '02' | '08'
    versionFestgelegt: boolean
    auszuege: { id: string; bezeichnung: string; zeitraum_von: string; zeitraum_bis: string; stand: string }[]
  }[]
  nichtBereit: number
  standardVersion: '02' | '08'
}

export function Exportieren({ mandantId, offen, aufSchliessen }: { mandantId: string; offen: boolean; aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const [format, setzeFormat] = useState<'camt053' | 'mt940'>('camt053')
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  const [fertig, setzeFertig] = useState<{ id: string; dateiname: string; dateien: number } | null>(null)
  const [anleitung, setzeAnleitung] = useState(false)

  const vorschau = useQuery({
    queryKey: ['exportvorschau', mandantId],
    queryFn: () => api.holen<Vorschau>(`/api/mandanten/${mandantId}/export`),
    enabled: offen,
    staleTime: 0,
  })

  const v = vorschau.data
  const auszuege = v?.konten.reduce((s, k) => s + k.auszuege.length, 0) ?? 0
  const ausland = v?.konten.some((k) => !k.inland) ?? false

  /* Fuer ein Auslandskonto gibt es keinen camt-Weg in DATEV. Das ist keine
     Wahl, also wird umgestellt statt gefragt. */
  useEffect(() => {
    if (ausland) setzeFormat('mt940')
  }, [ausland])

  function schliessen() {
    setzeFehler(null)
    setzeFertig(null)
    setzeAnleitung(false)
    aufSchliessen()
  }

  async function exportieren() {
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      const antwort = await api.senden<{ id: string; dateiname: string; dateien: number }>(`/api/mandanten/${mandantId}/export`, { format })
      setzeFertig(antwort)
      // Erst die Seite nachladen, dann den Download starten: Safari bricht
      // laufende Abfragen ab, sobald ein Download anlaeuft, und die Akte
      // meldete dann "Server nicht erreichbar", obwohl alles lief.
      await abfragen.invalidateQueries()
      herunterladen(antwort.id)
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : 'Der Export hat nicht geklappt.')
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel="Für DATEV exportieren" offen={offen} aufSchliessen={schliessen} breit>
      <div className="formular">
        {fehler && <div className="fehler">{fehler}</div>}

        {fertig ? (
          <>
            <div className="bestaetigung">
              Exportiert: {fertig.dateiname}
              {fertig.dateien > 1 ? `, ${fertig.dateien} Dateien in einem ZIP, eine je Auszug` : ''}. Der Download startet von selbst.
            </div>
            <button type="button" className="knopf" onClick={() => herunterladen(fertig.id)}>Noch einmal herunterladen</button>
            <p className="leiser">Die Datei liegt ab jetzt unter <Link to="/exporte" className="verweis" onClick={schliessen}>Exporte</Link> und lässt sich dort jederzeit erneut herunterladen.</p>
          </>
        ) : (
          <>
            {vorschau.isPending && <div className="leiser">Wird zusammengestellt …</div>}
            {v && v.konten.length === 0 && (
              <div className="leer-klein">
                <strong>Kein Auszug ist fertig.</strong>
                {v.nichtBereit > 0 ? ` ${v.nichtBereit} ${v.nichtBereit === 1 ? 'Auszug wartet' : 'Auszüge warten'} noch auf Prüfung.` : ''}
              </div>
            )}
            {v && v.konten.length > 0 && (
              <>
                <div className="exportkonten">
                  {v.konten.map((k) => (
                    <div key={k.iban} className="exportkonto">
                      <div className="exportkonto-kopf">
                        <span className="zahl">{iban(k.iban)}</span>
                        <span className="leiser">
                          {format === 'camt053' ? (k.inland ? `camt.053.001.${k.version}${k.versionFestgelegt ? ', wie beim letzten Export' : ''}` : 'Ausland, nur MT940') : 'MT940'}
                        </span>
                      </div>
                      {k.auszuege.map((a) => (
                        <div key={a.id} className="exportkonto-zeile">
                          <span>{a.bezeichnung}</span>
                          <span className="leiser zahl">{zeitraum(a.zeitraum_von, a.zeitraum_bis)}</span>
                          {a.stand === 'freigegeben' && <span className="marke">freigegeben</span>}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                {v.nichtBereit > 0 && (
                  <div className="leiser">{v.nichtBereit} weitere {v.nichtBereit === 1 ? 'Auszug wartet' : 'Auszüge warten'} noch auf Prüfung und {v.nichtBereit === 1 ? 'geht' : 'gehen'} nicht mit.</div>
                )}

                {/* camt ist der einzige Weg. MT940 gibt es nur noch still fuer
                    ein Konto im Ausland, weil DATEV dafuer kein camt annimmt. */}
                {ausland && (
                  <div className="hinweis">
                    Ein Konto liegt im Ausland. Dafür gibt es in DATEV keinen camt-Weg, exportiert wird MT940.
                    MT940 kürzt Verwendungszweck und Namen auf die Feldlängen des Formats.
                  </div>
                )}
              </>
            )}
          </>
        )}

        <button type="button" className="aufklapper" aria-expanded={anleitung} onClick={() => setzeAnleitung(!anleitung)}>
          So kommt die Datei in DATEV
        </button>
        {anleitung && <DatevAnleitung format={format} />}

        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={schliessen}>{fertig ? 'Schließen' : 'Abbrechen'}</button>
          {!fertig && (
            <button type="button" className="knopf gefuellt" disabled={laeuft || auszuege === 0 || (format === 'camt053' && ausland)} onClick={() => void exportieren()}>
              {laeuft ? 'Wird erzeugt …' : `${auszuege === 1 ? 'Einen Auszug' : `${auszuege} Auszüge`} exportieren`}
            </button>
          )}
        </div>
      </div>
    </Fenster>
  )
}

/** Der Muelleimer an einer Exportzeile. Fragt im eigenen Fenster nach, nie ueber den Browser. */
export function ExportLoeschen({ exportId, dateiname }: { exportId: string; dateiname: string }) {
  const abfragen = useQueryClient()
  const [offen, setzeOffen] = useState(false)
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  // Vorauswahl "Ja": wer nur schnell klickt, landet auf der sicheren Seite.
  const [importiert, setzeImportiert] = useState(true)

  async function loeschen() {
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await api.loeschen(`/api/exporte/${exportId}${importiert ? '?importiert=1' : ''}`)
      await abfragen.invalidateQueries()
      setzeOffen(false)
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : 'Der Export konnte nicht gelöscht werden.')
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <>
      <button type="button" className="knopf still klein symbolknopf" aria-label={`Export ${dateiname} löschen`} title="Export löschen" onClick={() => setzeOffen(true)}>
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2.5 4h11M6.5 4V2.5h3V4M4 4l.7 9.2c.05.7.6 1.3 1.3 1.3h4c.7 0 1.25-.6 1.3-1.3L12 4M6.8 7v4.5M9.2 7v4.5" />
        </svg>
      </button>
      <Fenster titel="Export löschen" offen={offen} aufSchliessen={() => setzeOffen(false)}>
        <div className="formular">
          {fehler && <div className="fehler">{fehler}</div>}
          <p className="wahlfrage-titel">Ist die Datei schon in DATEV importiert?</p>
          <div className="wahl" role="group" aria-label="Schon in DATEV importiert">
            <button type="button" aria-pressed={importiert} onClick={() => setzeImportiert(true)}>Ja</button>
            <button type="button" aria-pressed={!importiert} onClick={() => setzeImportiert(false)}>Nein</button>
          </div>
          <p className="leiser">
            {importiert
              ? 'Die Auszüge bleiben exportiert. Ein zweiter Export ist gesperrt.'
              : 'Die Auszüge stehen wieder zum Export bereit.'}
          </p>
          <div className="formular-fuss">
            <button type="button" className="knopf still" onClick={() => setzeOffen(false)}>Abbrechen</button>
            <button type="button" className="knopf gefuellt loeschknopf" disabled={laeuft} onClick={() => void loeschen()}>{laeuft ? 'Wird gelöscht …' : 'Löschen'}</button>
          </div>
        </div>
      </Fenster>
    </>
  )
}

export function herunterladen(exportId: string): void {
  const a = document.createElement('a')
  a.href = `/api/exporte/${exportId}/datei`
  a.download = ''
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}
